import type { AdminEvent } from '@mds/shared/types/event';

import { clockLabel } from '@mds/shared/lib/dates';

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A month of the events programme as a Monday-first grid. Days carrying an
 * event are tinted, and each event on a day is its own button.
 *
 * `month` is the `yyyy-mm` key the API filters on, so the grid and the query
 * are always looking at the same month.
 */
export default function CalendarGrid({
  month,
  events,
  onPick,
}: {
  month: string;
  events: AdminEvent[];
  onPick: (e: AdminEvent) => void;
}) {
  const [year, monthNumber] = month.split('-').map(Number);
  const index = (monthNumber ?? 1) - 1;
  const first = new Date(year ?? 2000, index, 1);
  const lead = (first.getDay() + 6) % 7;
  const total = new Date(year ?? 2000, index + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(lead).fill(null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];

  return (
    <div className="grid grid-cols-7 gap-1.5">
      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
        <span
          key={d}
          className="pb-1 text-center font-mono text-sm tracking-wider text-ink-muted uppercase"
        >
          {d}
        </span>
      ))}
      {cells.map((d, i) => {
        const key = d ? `${month}-${pad(d)}` : null;
        const evs = key ? events.filter((e) => e.eventDate === key) : [];
        return (
          <div
            key={i}
            className={
              'min-h-[92px] rounded-xl border p-2 ' +
              (d
                ? evs.length
                  ? 'border-brand-edge bg-brand-wash'
                  : 'border-hairline bg-surface'
                : 'border-transparent')
            }
          >
            {d && <span className="font-mono text-sm text-ink-muted">{d}</span>}
            <div className="mt-1 flex flex-col gap-1">
              {evs.map((e) => (
                <button
                  key={e.id}
                  onClick={() => onPick(e)}
                  className="truncate rounded-md bg-panel px-2 py-1 text-left text-sm font-semibold text-brand transition-transform duration-(--duration-fast) ease-standard hover:-translate-y-0.5"
                >
                  {clockLabel(e.startMinute)} {e.title}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
